import {
	AspectMaintainer, Button, ButtonTypes, Checkbox, Input, PopoverPlacement, Select, Switch
} from "@clintonelec/react-storybook";
import { produce } from "immer";
import type { Dispatch, SetStateAction } from "react";
import { Fragment, memo } from "react";
import "Components/ConfigLayout/Camera/OverlaySettings/LogoAlarmSettings/LogoAlarmSettings.less";
import UploadModal from "Components/ConfigLayout/Camera/OverlaySettings/UploadModal";
import { logoDisplayModeOptions } from "Data/Objects/BannerOverlay";
import type { BannerDisplayMode, OverlayState } from "Interfaces";

type LogoAlarmSettingsProps = {
	localOverlaySettings: OverlayState;
	setLocalOverlaySettings: Dispatch<SetStateAction<OverlayState>>;
};

function LogoAlarmSettings(props: LogoAlarmSettingsProps) {
	const { localOverlaySettings, setLocalOverlaySettings } = props;
	const { logoAlarmOverlay, enableOverlays: mainToggle } = localOverlaySettings;
	const {
		enableOverlay, file, displayMode
	} = logoAlarmOverlay;

	const validateFile = async (newFile: File) => {
		if (newFile.size > 512000) {
			return false;
		}

		const image = await createImageBitmap(newFile);

		if (image.width > 1920 || image.height > 1080) {
			return false;
		}

		return true;
	};

	const handleEnableOverlayChange = (newEnableOverlay: boolean) => {
		setLocalOverlaySettings(produce(localOverlaySettings, draft => {
			draft.logoAlarmOverlay.enableOverlay = newEnableOverlay;
		}));
	};

	const uploadFile = (name: string, dataUrl: string) => {
		setLocalOverlaySettings(produce(localOverlaySettings, draft => {
			draft.logoAlarmOverlay.file = { name, dataUrl };
		}));
	};

	const handleFileUpdate = (newFile: string) => {
		setLocalOverlaySettings(produce(localOverlaySettings, draft => {
			if (!newFile && draft.logoAlarmOverlay.file !== null) {
				draft.logoAlarmOverlay.file = null;
			}
		}));
	};

	const handleDisplayModeSelect = (newDisplayMode: string | null) => {
		setLocalOverlaySettings(produce(localOverlaySettings, draft => {
			draft.bannerOverlay.displayMode = newDisplayMode as BannerDisplayMode;
		}));
	};

	const logoOverlayWrapper = (
		<div className="logo-overlay-wrap">
			<div className="logo">
				<img className="image" src={ file?.dataUrl } />
			</div>
		</div>
	);

	const renderLogoAlarmSettings = () => {
		if (!enableOverlay) {
			return <div className="enable-message">Enable Logo Alarm to adjust settings.</div>;
		}

		const restrictions = `
			Please upload image files in .png or .jpg formats that are under 512KB.
			Files must be 1920px wide and 1080px tall (1920 x 1080).
		`;

		return (
			<Fragment>
				<div className="form-row">
					<span>File 1</span>
					<div className="file-select-wrapper">
						<UploadModal
							uploadFile={ uploadFile }
							restrictions={ restrictions }
							validateFile={ validateFile }
						>
							<Button
								htmlType="button"
								type={ ButtonTypes.TERTIARY }
							>
								Browse
							</Button>
						</UploadModal>
						<Input
							disabled
							placeholder="Upload a file"
							value={ file?.name ?? "" }
							clearable
							wrapClassName="file-select-input-wrapper"
							onUpdate={ handleFileUpdate }
						/>
					</div>
				</div>
				<div className="form-row">
					<span>Display Mode</span>
					<Select
						className="form-select"
						allowClear={ false }
						menuShouldBlockScroll
						menuPlacement={ PopoverPlacement.AUTO }
						menuPortalTarget={ document.body }
						onSelect={ handleDisplayModeSelect }
						options={ logoDisplayModeOptions }
						value={ displayMode }
					/>
				</div>
				<div className="form-row">
					<span><span className="inset">Event Type</span></span>
					<div className="event-types">
						<Checkbox
							checked
							disabled
						>
							<span>Alarm In</span>
						</Checkbox>
					</div>
				</div>
			</Fragment>
		);
	};

	const renderLogoControls = () => {
		if (!mainToggle) {
			return <div className="enable-message">Enable Overlays to adjust settings.</div>;
		}

		return (
			<Fragment>
				<div className="form-row">
					<Switch
						className="enable-overlay-switch"
						checked={ enableOverlay }
						onChange={ handleEnableOverlayChange }
					/>
					<span>Enable Logo Alarm</span>
				</div>
				{ renderLogoAlarmSettings() }
			</Fragment>
		);
	};

	return (
		<div className="logo-alarm-overlay-settings columns">
			<div className="column">
				<AspectMaintainer>
					<div className={ `live-snapshot` }>
						<AspectMaintainer>
							<div className="preview-wrapper border">
								<div className="camera-snapshot" />
							</div>
						</AspectMaintainer>
						{ logoOverlayWrapper }
					</div>
				</AspectMaintainer>
			</div>
			<div className="column">
				{ renderLogoControls() }
			</div>
		</div>
	);
}

export default memo(LogoAlarmSettings);
